<!--
  THE MARK OF A PRIZE — the official emblem now, ours underneath.

  The first pass drew only abstract marks of our own (laurel, wreath, palm,
  star) because the real insignia are trademarks the academies enforce. THE
  OWNER'S DECISION (2026-08-17) reverses that for this app: it is private,
  invite-only and non-commercial, and he explicitly accepted the trademark and
  copyright risk — so the badge now wears each award's official emblem, sourced
  and recorded in web/public/awards/README.md. Two rules keep it honest:

    • The emblem is rendered as an ALPHA MASK in the award's own tone, not as
      the emblem's artwork — one silhouette language across seven insignia,
      legible on the glass plate in every theme, no tone-aware plate needed.
    • OUR GLYPHS STAY, drawn below as the automatic fallback whenever an asset
      is missing or fails its probe — the badge never renders broken, and
      reverting the whole decision is deleting a directory.

  Each mark was rasterised and looked at before shipping, which was not a
  formality: the first home-drawn wreath rendered as a plain circle, and an
  emblem that only works by internal light-vs-dark contrast turns to mush as a
  mask — the contact-sheet check in the awards README exists for exactly that.

  THE EXPANDED BADGE: on an open card (the tile grown into its 16:9 trailer)
  the badge expands with it — the initials give way to the award's precise
  colloquial name ("Best Picture", "Golden Globe — Best TV Drama"; see
  awards.ts for why it is never a vaguer parent), sliding out on the same
  one-clock system as the card: 240ms structure, 130ms fade, --ease-out. The
  caller passes `expanded` from its own open state so badge and card cannot
  drift apart.
-->
<script lang="ts" module>
  import { SvelteMap } from 'svelte/reactivity';

  /**
   * One probe per emblem file for the whole page, shared by every badge: a
   * rail of ten Oscar winners must not race ten Image() loads for one asset,
   * and a 404 must be learned once, not re-fetched per tile. `undefined`
   * means "probing or never asked" and draws the fallback glyph — the emblem
   * is an upgrade that arrives, never a hole while it loads.
   */
  const probed = new SvelteMap<string, boolean>();
  const probing = new Set<string>();

  function probe(src: string): void {
    if (probing.has(src) || typeof Image === 'undefined') return;
    probing.add(src);
    const img = new Image();
    img.onload = () => probed.set(src, true);
    img.onerror = () => probed.set(src, false);
    img.src = src;
  }
</script>

<script lang="ts">
  import type { Award } from './awards';

  interface Props {
    award: Award;
    size?: number;
    /**
     * The open-card state: the emblem keeps its place and the colloquial name
     * slides out where the initials were. Passed by the owner of the card's
     * own `open` state (Poster's `showTrailer`), never derived here.
     */
    expanded?: boolean;
  }

  let { award, size = 26, expanded = false }: Props = $props();

  const src = $derived(`/awards/${award.emblem}`);
  $effect(() => probe(src));
  const emblemOk = $derived(probed.get(src) === true);

  /*
   * The mask box follows the emblem's own proportions (a mask has no intrinsic
   * size), clamped so the 2.74:1 Palme frond cannot flatten the badge and the
   * 0.38:1 Oscar statuette still earns a sliver of breathing room.
   */
  const boxAspect = $derived(Math.min(1.7, Math.max(0.45, award.emblemAspect)));
</script>

<span
  class="award"
  class:expanded
  style:--tone={award.tone}
  style:--s="{size}px"
  title="{award.label} — winner"
  aria-label="{award.label} — winner"
  role="img"
>
  {#if emblemOk}
    <i class="emblem" style:--emblem="url('{src}')" style:--ar={boxAspect}></i>
  {:else}
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {#if award.shape === 'laurel'}
        <!-- Two sprigs curving up to meet. A laurel is the oldest way of saying
             "this one won" and it belongs to nobody. -->
        <path d="M12 20.6C7.6 19 5 15.2 5.4 10.2" />
        <path d="M12 20.6c-2.6-1.8-3.6-4.6-3-7.8" />
        <path d="M12 20.6c4.4-1.6 7-5.4 6.6-10.4" />
        <path d="M12 20.6c2.6-1.8 3.6-4.6 3-7.8" />
      {:else if award.shape === 'wreath'}
        <!-- A closed ring of the same idea: a circle broken at the top. -->
        <path d="M12 20.6c-4.6-.9-8-4.4-8-8.6 0-3.1 1.9-5.8 4.8-7.2" />
        <path d="M12 20.6c4.6-.9 8-4.4 8-8.6 0-3.1-1.9-5.8-4.8-7.2" />
      {:else if award.shape === 'palm'}
        <!-- A frond: Cannes, without Cannes' actual leaf. -->
        <path d="M12 21v-8.6" />
        <path d="M12 12.4C8.8 11.6 6.6 8.8 6.4 4.6c3.4.7 5.4 3 5.6 6.4" />
        <path d="M12 12.4c3.2-.8 5.4-3.6 5.6-7.8-3.4.7-5.4 3-5.6 6.4" />
      {:else}
        <!-- A star, for television. -->
        <path d="M12 3.4l2.6 5.6 6 .8-4.4 4.2 1.1 6.1L12 17.2 6.7 20.1l1.1-6.1L3.4 9.8l6-.8z" />
      {/if}
    </svg>
  {/if}
  <!--
    Two labels, one visible: the initials at rest, the colloquial name on the
    open card. Both are 0fr↔1fr grid columns (the one way to animate to
    intrinsic width without measuring it), so the badge grows and shrinks as
    one continuous shape instead of swapping between two.
  -->
  <b class="tag code" class:show={!expanded}><span>{award.code}</span></b>
  <b class="tag full" class:show={expanded}><span>{award.colloquial}</span></b>
</span>

<style>
  .award {
    display: inline-flex;
    align-items: center;
    padding: 2px 5px 2px 3px;
    border-radius: 5px;
    /* The same blurred plate the rating badge uses, so two badges on one poster
       read as one family rather than as two different ideas. */
    background: color-mix(in oklab, var(--bg) 74%, transparent);
    backdrop-filter: blur(8px) saturate(1.3);
    box-shadow: var(--glass-shine);
    color: var(--tone);
    line-height: 1;
    pointer-events: none;
  }
  /*
   * The official emblem, as a silhouette in the award's tone: the mask uses
   * only the file's alpha, so a black Commons SVG and a full-colour PNG both
   * arrive as the same one-colour mark — which is what stays legible at 13px
   * on the glass, whatever the theme behind it. Slightly larger box than the
   * stroke glyph's 0.52 because real insignia carry their own inner margins.
   */
  .emblem {
    display: block;
    width: calc(var(--s) * 0.62 * var(--ar, 1));
    height: calc(var(--s) * 0.62);
    background: var(--tone);
    -webkit-mask: var(--emblem) center / contain no-repeat;
    mask: var(--emblem) center / contain no-repeat;
  }
  .award svg {
    width: calc(var(--s) * 0.52);
    height: calc(var(--s) * 0.52);
    display: block;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.75;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  /* The star is a solid shape rather than an outline; it reads as a blob at
     11px otherwise. */
  .award svg path[d^='M12 3.4l'] {
    fill: currentColor;
    stroke-width: 1;
  }
  /*
   * The expanding label. Grid 0fr→1fr is the horizontal twin of the details
   * strip's reveal (Rail.svelte): the column animates to intrinsic width with
   * no JavaScript measuring it. The 3px gap lives INSIDE the sliding cell as
   * the span's padding, so a collapsed label costs zero width — a flex gap
   * would keep charging for it at 0fr.
   *
   * One clock: 240ms structure, 130ms fade, --ease-out — the same numbers the
   * open card itself grows on, because badge and card are one movement.
   */
  .tag {
    display: inline-grid;
    grid-template-columns: 0fr;
    opacity: 0;
    transition:
      grid-template-columns 240ms var(--ease-out),
      opacity 130ms var(--ease-out);
  }
  .tag > span {
    overflow: hidden;
    min-width: 0;
    white-space: nowrap;
    padding-left: 3px;
  }
  .tag.show {
    grid-template-columns: 1fr;
    opacity: 1;
  }
  .code {
    font-size: var(--text-2xs);
    font-weight: var(--weight-bold);
    letter-spacing: 0.02em;
    color: var(--text);
  }
  /* `.full`, not `.name` — Poster's caption owns that word in this DOM. */
  .full {
    font-size: var(--text-xs);
    font-weight: var(--weight-semi);
    letter-spacing: 0.01em;
    color: var(--text);
  }
  @media (prefers-reduced-motion: reduce) {
    .tag {
      transition: none;
    }
  }
</style>
